import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { JobCycleData, WORKFLOW_STAGES, computeStageBreakdown } from "@/hooks/useCycleTimeAnalytics";
import { format } from "date-fns";
import { toast } from "sonner";

interface CycleTimeExportButtonProps {
  data: JobCycleData[];
  startDate: Date;
  endDate: Date;
  isLoading: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  print_materials: 'Print Materials',
  packaging: 'Packaging',
  promotional_products: 'Promotional Products',
  signage: 'Signage',
  branded_merchandise: 'Branded Merchandise',
};

export function CycleTimeExportButton({ data, startDate, endDate, isLoading }: CycleTimeExportButtonProps) {
  const handleExport = () => {
    if (data.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      // Build CSV content
      const lines: string[] = [];

      // Header with metadata
      lines.push(`Cycle Time Analytics Report`);
      lines.push(`Date Range: ${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`);
      lines.push(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`);
      lines.push(`Total Jobs: ${data.length}`);
      lines.push('');

      // Column headers for job detail
      const headers = [
        'Job ID',
        'Job Title',
        'Category',
        'Requester',
        'Vendor',
        'Created Date',
        'Accepted Date',
        ...WORKFLOW_STAGES.map(s => `${s.label} (days)`),
        'Total Cycle Time (days)',
      ];
      lines.push(headers.join(','));

      // Job data rows
      data.forEach(job => {
        const row = [
          `"${job.jobPublicId}"`,
          `"${job.jobTitle.replace(/"/g, '""')}"`,
          `"${CATEGORY_LABELS[job.category] || job.category}"`,
          `"${job.requesterName.replace(/"/g, '""')}"`,
          `"${(job.vendorName || 'N/A').replace(/"/g, '""')}"`,
          format(job.createdAt, 'yyyy-MM-dd'),
          format(job.acceptedAt, 'yyyy-MM-dd'),
          ...WORKFLOW_STAGES.map(s => job.stageDurations[s.key].toFixed(1)),
          job.totalCycleTime.toFixed(1),
        ];
        lines.push(row.join(','));
      });

      // Summary section
      lines.push('');
      lines.push('SUMMARY STATISTICS');
      lines.push('');

      const breakdown = computeStageBreakdown(data);
      const totalCycleTimes = data.map(j => j.totalCycleTime);
      const avgTotal = totalCycleTimes.reduce((a, b) => a + b, 0) / totalCycleTimes.length;
      const sortedTotals = [...totalCycleTimes].sort((a, b) => a - b);
      const medianTotal = sortedTotals.length % 2 !== 0 
        ? sortedTotals[Math.floor(sortedTotals.length / 2)]
        : (sortedTotals[sortedTotals.length / 2 - 1] + sortedTotals[sortedTotals.length / 2]) / 2;

      lines.push(`Stage,Average (days),Median (days),% of Total`);
      breakdown.forEach(stage => {
        lines.push(`"${stage.label}",${stage.avgDays.toFixed(1)},${stage.medianDays.toFixed(1)},${stage.percentOfTotal.toFixed(1)}%`);
      });
      lines.push('');
      lines.push(`Total,${avgTotal.toFixed(1)},${medianTotal.toFixed(1)},100%`);

      // Create and download file
      const csvContent = lines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cycle-time-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${data.length} jobs to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={isLoading || data.length === 0}
      className="gap-2"
    >
      <Download className="h-4 w-4" />
      Export CSV
    </Button>
  );
}
